import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faFolderOpen,
  faTrashAlt,
  faUpload,
} from '@fortawesome/free-solid-svg-icons'
import DynamicTable from 'Components/DynamicTable/DynamicTable'
import MiniLoading from 'Components/MiniLoading'
import CustomSnackbar from 'Components/CustomSnackbar'
import QuickListFilters from './components/QuickListFilters'
import DeleteQuickListModal from './components/DeleteQuickListModal'
import ShowProductsModal from './components/ShowProductsModal'
import useColumnSearchQuickList from './hooks/useColumnSearchQuickList'
import {
  BATCH_ENTITY_ID,
  BATCH_ENTITY_TYPE,
  BATCH_TENANT_ID,
  QUICK_LIST_BATCH_TEMPLATE_ID,
  QUICK_LIST_PAGE_SIZE_OPTIONS,
  QUICK_LIST_ROUTES,
} from './constants/quickList.constants'
import {
  buildQuickListSearchPayload,
  buildQuickListBatchUploadFormData,
  downloadBlobFile,
  extractBulkResult,
  extractQuickListItems,
  extractUploadedBatchFile,
  extractQuickListTotal,
} from './utils/quickList.utils'
import {
  searchQuickListSettingsAction,
  getQuickListSettingAction,
  deleteQuickListSettingAction,
  downloadBatchTemplateFileAction,
  uploadQuickListBatchFileAction,
  batchProcessQuickListFileAction,
} from 'redux/actions/quickListSettingsActions'
import './SearchQuickList.css'

const WINDOW_SIZE = 3
const QUICK_LIST_TEMPLATE_FILE_NAME = 'PlantillaListasRapidas.csv'

const SearchQuickList = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const fileInputRef = useRef(null)
  const searchDebounceTimeoutRef = useRef(null)
  const userEmail = useSelector(
    state => state.login?.user?.AuthenticationInfo?.Email || ''
  )

  const [items, setItems] = useState([])
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [searchDebouncePending, setSearchDebouncePending] = useState(false)
  const [status, setStatus] = useState(true)
  const [loadingData, setLoadingData] = useState(false)
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false)
  const [selectedBulkFile, setSelectedBulkFile] = useState(null)
  const [uploadedBulkFile, setUploadedBulkFile] = useState(null)
  const [bulkResult, setBulkResult] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [isUploadingBulkFile, setIsUploadingBulkFile] = useState(false)
  const [isProcessingBulkFile, setIsProcessingBulkFile] = useState(false)
  const isBulkOperationInProgress = isUploadingBulkFile || isProcessingBulkFile

  const [previewItem, setPreviewItem] = useState(null)
  const [loadingPreviewProducts, setLoadingPreviewProducts] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showModalDeleteQuickList, setShowModalDeleteQuickList] =
    useState(false)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  })
  const [knownLastPage, setKnownLastPage] = useState(1)
  const [pageWindowFirst, setPageWindowFirst] = useState(0)
  const [pageWindowLast, setPageWindowLast] = useState(WINDOW_SIZE)
  const [searchToken, setSearchToken] = useState(0)

  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const searchRequestIdRef = useRef(0)
  const previewRequestIdRef = useRef(0)

  const pageNumbers = Array.from({ length: knownLastPage }, (_, i) => i + 1)

  const resetPagination = useCallback((forceSearch = true) => {
    setPagination(prev =>
      prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }
    )
    setKnownLastPage(1)
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    // Explicit filter actions can force a search even when all query values
    // remain unchanged (e.g., clearing filters from the default page).
    if (forceSearch) setSearchToken(prev => prev + 1)
  }, [])

  const goToLastValidPage = useCallback(lastValidPage => {
    const safePage = Math.max(1, lastValidPage)
    setPagination(prev => ({ ...prev, currentPage: safePage }))
    setKnownLastPage(safePage)
    setPageWindowFirst(prev => Math.min(prev, Math.max(0, safePage - 1)))
    setPageWindowLast(safePage)
  }, [])

  const searchQuickLists = useCallback(async () => {
    const requestId = ++searchRequestIdRef.current
    setLoadingData(true)

    try {
      const payload = buildQuickListSearchPayload({
        pagination,
        status,
        searchText: debouncedSearchText,
      })
      const data = await dispatch(searchQuickListSettingsAction(payload))

      if (requestId !== searchRequestIdRef.current) return

      const pageItems = extractQuickListItems(data)
      const total = extractQuickListTotal(data)

      if (pageItems.length === 0 && pagination.currentPage > 1) {
        if (total !== null) {
          goToLastValidPage(Math.max(1, Math.ceil(total / pagination.pageSize)))
        } else {
          goToLastValidPage(pagination.currentPage - 1)
        }
        return
      }

      setItems(pageItems)

      if (total !== null) {
        setKnownLastPage(Math.max(1, Math.ceil(total / pagination.pageSize)))
      } else if (pageItems.length === pagination.pageSize) {
        setKnownLastPage(prev => Math.max(prev, pagination.currentPage + 1))
      } else {
        setKnownLastPage(pagination.currentPage)
      }
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return
      setItems([])
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_LOAD'),
        severity: 'error',
      })
    } finally {
      if (requestId === searchRequestIdRef.current) setLoadingData(false)
    }
  }, [
    pagination.currentPage,
    pagination.pageSize,
    status,
    debouncedSearchText,
    searchToken,
    dispatch,
    goToLastValidPage,
  ])

  useEffect(() => {
    if (searchDebouncePending) return

    searchQuickLists()
  }, [searchQuickLists, searchDebouncePending])

  useEffect(() => () => clearTimeout(searchDebounceTimeoutRef.current), [])

  const handleClose = () => {
    setSnackbarMessage(prev => ({ ...prev, open: false }))
  }

  const handleClearSearch = () => {
    clearTimeout(searchDebounceTimeoutRef.current)
    searchDebounceTimeoutRef.current = null
    setSearchDebouncePending(false)
    setSearchText('')
    setDebouncedSearchText('')
    resetPagination()
  }

  const handleToggleBulkPanel = () => {
    if (isBulkOperationInProgress) return

    setBulkPanelOpen(prev => {
      if (prev) {
        setSelectedBulkFile(null)
        setUploadedBulkFile(null)
        setBulkResult(null)
        setUploadProgress(0)
      }

      return !prev
    })
  }

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true)

    try {
      const response = await dispatch(
        downloadBatchTemplateFileAction(QUICK_LIST_BATCH_TEMPLATE_ID)
      )

      downloadBlobFile({
        blob: response.data,
        fileName: QUICK_LIST_TEMPLATE_FILE_NAME,
      })
    } catch (error) {
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.FAIL_TEMPLATE'),
        severity: 'error',
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const handleBulkFileSelected = async event => {
    const [file] = event.target.files || []

    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      event.target.value = ''
      setSelectedBulkFile(null)
      setUploadedBulkFile(null)
      setBulkResult(null)
      setUploadProgress(0)
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.INVALID_FILE'),
        severity: 'error',
      })
      return
    }

    setSelectedBulkFile(file)
    setUploadedBulkFile(null)
    setBulkResult(null)
    setUploadProgress(0)
    setIsUploadingBulkFile(true)

    try {
      const uploadPayload = buildQuickListBatchUploadFormData({
        file,
        batchEntityId: BATCH_ENTITY_ID,
        tenantId: BATCH_TENANT_ID,
        entityType: BATCH_ENTITY_TYPE,
      })
      const uploadResponse = await dispatch(
        uploadQuickListBatchFileAction({
          payload: uploadPayload,
          onUploadProgress: progressEvent => {
            if (!progressEvent.total) return

            setUploadProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            )
          },
        })
      )
      const uploadedBatchFile = extractUploadedBatchFile({
        uploadResponse,
        batchEntityId: BATCH_ENTITY_ID,
      })

      if (!uploadedBatchFile.fileUrl) {
        throw new Error(
          t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.FAIL_FILE_UPLOAD')
        )
      }

      setUploadedBulkFile({ ...uploadedBatchFile })
      setUploadProgress(100)
      setSnackbarMessage({
        open: true,
        message: t(
          'QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.SUCCESS_FILE_UPLOAD'
        ),
        severity: 'success',
      })
    } catch {
      event.target.value = ''
      setSelectedBulkFile(null)
      setUploadedBulkFile(null)
      setUploadProgress(0)
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.FAIL_FILE_UPLOAD'),
        severity: 'error',
      })
    } finally {
      setIsUploadingBulkFile(false)
    }
  }

  const handleRemoveBulkFile = () => {
    setSelectedBulkFile(null)
    setUploadedBulkFile(null)
    setBulkResult(null)
    setUploadProgress(0)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleStartBulkUpload = async () => {
    if (!uploadedBulkFile?.fileUrl) return

    setIsProcessingBulkFile(true)

    try {
      const processPayload = {
        templateId: QUICK_LIST_BATCH_TEMPLATE_ID,
        url: uploadedBulkFile.fileUrl,
        batchProcessId: uploadedBulkFile.batchProcessId,
        email: userEmail,
      }
      const response = await dispatch(
        batchProcessQuickListFileAction({ payload: processPayload })
      )

      const responseStatus = response?.status
      const batchSegments = response?.data

      if (responseStatus && responseStatus !== 202) {
        throw new Error(
          t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.FAIL_UPLOAD')
        )
      }

      setBulkResult(extractBulkResult(batchSegments))
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.SUCCESS_UPLOAD'),
        severity: 'success',
      })
      setSelectedBulkFile(null)
      setUploadedBulkFile(null)
      setUploadProgress(0)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSearchToken(prev => prev + 1)
    } catch {
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.BULK_UPLOAD.MESSAGE.FAIL_UPLOAD'),
        severity: 'error',
      })
    } finally {
      setIsProcessingBulkFile(false)
    }
  }

  const handleSearchTextChange = value => {
    clearTimeout(searchDebounceTimeoutRef.current)
    setSearchText(value)
    setSearchDebouncePending(true)
    resetPagination(false)

    searchDebounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(value)
      setSearchDebouncePending(false)
      searchDebounceTimeoutRef.current = null
    }, 500)
  }

  const handlePageSizeChange = event => {
    const nextPageSize = Number(event.target.value)
    resetPagination()
    setPagination(prev => ({
      ...prev,
      pageSize: nextPageSize,
    }))
  }

  const handlePrevPage = () => {
    if (pagination.currentPage <= 1) return
    setPageWindowFirst(prev => Math.max(0, prev - 1))
    setPageWindowLast(prev => Math.max(WINDOW_SIZE, prev - 1))
    setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))
  }

  const handleNextPage = () => {
    if (pagination.currentPage >= knownLastPage) return
    setPageWindowFirst(prev =>
      Math.max(0, Math.min(pageNumbers.length - WINDOW_SIZE, prev + 1))
    )
    setPageWindowLast(prev => Math.min(pageNumbers.length, prev + 1))
    setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))
  }

  const handlePageClick = e => {
    const page = parseInt(e.target.value)
    setPageWindowFirst(page - 1)
    setPageWindowLast(page - 1 + WINDOW_SIZE)
    setPagination(prev => ({ ...prev, currentPage: page }))
  }

  const handleReturnFirst = () => {
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const handleJumpForward = () => {
    const newFirst = pageWindowLast
    const newLast = Math.min(pageNumbers.length, pageWindowLast + WINDOW_SIZE)
    setPageWindowFirst(newFirst)
    setPageWindowLast(newLast)
    setPagination(prev => ({ ...prev, currentPage: pageWindowLast + 1 }))
  }

  const handleViewProducts = async item => {
    const requestId = ++previewRequestIdRef.current
    setPreviewItem(item)
    setLoadingPreviewProducts(true)

    try {
      const detail = await dispatch(
        getQuickListSettingAction(item.ProductCustomListId)
      )

      if (requestId !== previewRequestIdRef.current) return

      setPreviewItem(prev =>
        prev?.ProductCustomListId === item.ProductCustomListId
          ? { ...prev, ...detail }
          : prev
      )
      setLoadingPreviewProducts(false)
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) return
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_LOAD'),
        severity: 'error',
      })
      setLoadingPreviewProducts(false)
    }
  }

  const handleDelete = item => {
    setDeleteItem(item)
    setShowModalDeleteQuickList(true)
  }

  const handleConfirmDeleteQuickList = async () => {
    if (!deleteItem) return

    try {
      const success = await dispatch(
        deleteQuickListSettingAction(deleteItem.ProductCustomListId)
      )

      if (success) {
        setSnackbarMessage({
          open: true,
          message: t('QUICK_LIST_SETTINGS.MESSAGE.SUCCESS_DELETE'),
          severity: 'success',
        })
        setDeleteItem(null)
        searchQuickLists()
      } else {
        setSnackbarMessage({
          open: true,
          message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_DELETE'),
          severity: 'error',
        })
      }
    } catch (error) {
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_DELETE'),
        severity: 'error',
      })
    } finally {
      setShowModalDeleteQuickList(false)
    }
  }

  const columns = useColumnSearchQuickList({
    onViewProducts: handleViewProducts,
    onEdit: item =>
      history.push(QUICK_LIST_ROUTES.EDIT(item.ProductCustomListId)),
    onDelete: handleDelete,
    pagination,
  })

  return (
    <>
      <div className="search-quick-list" data-testid="container-quick-list">
        <QuickListFilters
          searchText={searchText}
          status={status}
          loading={loadingData}
          onSearchTextChange={handleSearchTextChange}
          onStatusChange={newStatus => {
            setStatus(newStatus)
            resetPagination()
          }}
          onClearSearch={handleClearSearch}
          onCreate={() => history.push(QUICK_LIST_ROUTES.CREATE)}
          onToggleBulkUpload={handleToggleBulkPanel}
          bulkOperationInProgress={isBulkOperationInProgress}
        />

        {bulkPanelOpen && (
          <div
            className="quick-list-bulk-panel"
            data-testid="container-quick-list-bulk-upload">
            <h2>{t('QUICK_LIST_SETTINGS.BULK_UPLOAD.TITLE')}</h2>

            <div className="quick-list-bulk-instructions">
              <strong>
                {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.INSTRUCTIONS_TITLE')}
              </strong>
              <ul>
                {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.INSTRUCTIONS', {
                  returnObjects: true,
                }).map(instruction => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ul>
            </div>

            <div className="quick-list-bulk-file-actions">
              <button
                type="button"
                className="btn-clean btn-download-template"
                disabled={
                  isDownloadingTemplate ||
                  isUploadingBulkFile ||
                  isProcessingBulkFile
                }
                onClick={handleDownloadTemplate}
                data-testid="btn-download-quick-list-template">
                <FontAwesomeIcon icon={faDownload} />
                {isDownloadingTemplate
                  ? t('QUICK_LIST_SETTINGS.BULK_UPLOAD.DOWNLOADING_TEMPLATE')
                  : t('QUICK_LIST_SETTINGS.BULK_UPLOAD.DOWNLOAD_TEMPLATE')}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={handleBulkFileSelected}
                data-testid="input-quick-list-bulk-file"
              />

              <button
                type="button"
                className="btn-clean btn-select-bulk-file"
                disabled={isUploadingBulkFile || isProcessingBulkFile}
                onClick={() => fileInputRef.current?.click()}
                data-testid="btn-select-quick-list-file">
                <FontAwesomeIcon icon={faFolderOpen} />
                {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.SELECT_FILE')}
              </button>

              <span className="quick-list-bulk-file-name">
                {selectedBulkFile?.name ||
                  t('QUICK_LIST_SETTINGS.BULK_UPLOAD.NO_FILE_SELECTED')}
              </span>

              {selectedBulkFile && (
                <button
                  type="button"
                  className="quick-list-bulk-remove-file"
                  disabled={isUploadingBulkFile || isProcessingBulkFile}
                  onClick={handleRemoveBulkFile}
                  title={t('QUICK_LIST_SETTINGS.LIST.DELETE')}
                  aria-label={t('QUICK_LIST_SETTINGS.LIST.DELETE')}
                  data-testid="btn-remove-quick-list-bulk-file">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              )}
            </div>

            {isUploadingBulkFile && (
              <div
                className="quick-list-upload-progress"
                data-testid="container-quick-list-upload-progress">
                <div className="quick-list-upload-progress-header">
                  <span>
                    {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.UPLOADING_FILE')}
                  </span>
                  <b>{uploadProgress}%</b>
                </div>
                <div className="quick-list-upload-progress-track">
                  <div
                    className="quick-list-upload-progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="quick-list-bulk-submit-actions">
              <button
                type="button"
                className="btn-add btn-start-bulk-upload"
                disabled={
                  !uploadedBulkFile?.fileUrl ||
                  isUploadingBulkFile ||
                  isProcessingBulkFile
                }
                onClick={handleStartBulkUpload}
                data-testid="btn-start-quick-list-bulk-upload">
                <FontAwesomeIcon icon={faUpload} />
                {isProcessingBulkFile
                  ? t('QUICK_LIST_SETTINGS.BULK_UPLOAD.UPLOADING')
                  : t('QUICK_LIST_SETTINGS.BULK_UPLOAD.START_UPLOAD')}
              </button>

              <button
                type="button"
                className="btn-clean"
                disabled={isUploadingBulkFile || isProcessingBulkFile}
                onClick={handleToggleBulkPanel}
                data-testid="btn-cancel-quick-list-bulk-upload">
                {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.CANCEL')}
              </button>
            </div>

            {bulkResult && (
              <div
                className="quick-list-bulk-result"
                data-testid="container-quick-list-bulk-result">
                <div className="quick-list-bulk-summary">
                  <span>
                    <b>{t('QUICK_LIST_SETTINGS.BULK_UPLOAD.SUMMARY.TOTAL')}:</b>{' '}
                    {bulkResult.TotalRows || 0}
                  </span>
                  <span className="text-success">
                    <b>
                      {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.SUMMARY.SUCCESS')}:
                    </b>{' '}
                    {bulkResult.SuccessRows || 0}
                  </span>
                  <span className="text-danger">
                    <b>
                      {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.SUMMARY.FAILED')}:
                    </b>{' '}
                    {bulkResult.FailedRows || 0}
                  </span>
                </div>

                {bulkResult.Errors?.length > 0 && (
                  <>
                    <h3>
                      {t('QUICK_LIST_SETTINGS.BULK_UPLOAD.ERROR_REPORT_TITLE')}
                    </h3>
                    <div className="quick-list-bulk-errors-wrapper">
                      <table className="quick-list-bulk-errors-table">
                        <thead>
                          <tr>
                            <th>
                              {t(
                                'QUICK_LIST_SETTINGS.BULK_UPLOAD.ERROR_REPORT.ROW'
                              )}
                            </th>
                            <th>
                              {t(
                                'QUICK_LIST_SETTINGS.BULK_UPLOAD.ERROR_REPORT.FIELD'
                              )}
                            </th>
                            <th>
                              {t(
                                'QUICK_LIST_SETTINGS.BULK_UPLOAD.ERROR_REPORT.VALUE'
                              )}
                            </th>
                            <th>
                              {t(
                                'QUICK_LIST_SETTINGS.BULK_UPLOAD.ERROR_REPORT.REASON'
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResult.Errors.map(error => (
                            <tr
                              key={`${error.RowNumber}-${error.Field}-${error.Value}`}>
                              <td>{error.RowNumber}</td>
                              <td>{error.Field}</td>
                              <td>{error.Value}</td>
                              <td>{error.Reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {loadingData ? (
          <MiniLoading />
        ) : (
          <DynamicTable
            columns={columns}
            data={items}
            loadingData={loadingData}
          />
        )}

        <div
          className="search-quick-list-footer"
          data-testid="container-pagination">
          <div className="footer-content">
            <p>
              {t('QUICK_LIST_SETTINGS.LIST.SHOWING_PAGE', {
                currentPage: pagination.currentPage,
              })}
            </p>

            <select
              data-testid="input-page-size"
              className="page-size-selector"
              onChange={handlePageSizeChange}
              value={pagination.pageSize}>
              {QUICK_LIST_PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="quick-list-pagination">
            <button
              className="btn-quick-list-prev-page"
              data-testid="btn-prev-page"
              disabled={pagination.currentPage <= 1 || loadingData}
              onClick={handlePrevPage}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                style={{ fontSize: '1.8rem' }}
              />
            </button>

            {pageWindowFirst > 0 && (
              <button
                className="btn-quick-list-regular-page"
                onClick={handleReturnFirst}>
                ...
              </button>
            )}

            {pageNumbers.slice(pageWindowFirst, pageWindowLast).map(pageNum => (
              <button
                data-testid={`btn-page-${pageNum}`}
                key={pageNum}
                className={
                  pagination.currentPage === pageNum
                    ? 'btn-quick-list-active-page'
                    : 'btn-quick-list-regular-page'
                }
                value={pageNum}
                onClick={handlePageClick}
                disabled={loadingData}>
                {pageNum}
              </button>
            ))}

            {pageWindowLast < pageNumbers.length && (
              <button
                className="btn-quick-list-regular-page"
                onClick={handleJumpForward}>
                ...
              </button>
            )}

            <button
              className="btn-quick-list-next-page"
              data-testid="btn-next-page"
              disabled={pagination.currentPage >= knownLastPage || loadingData}
              onClick={handleNextPage}>
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: '1.8rem' }}
              />
            </button>
          </div>
        </div>

        <ShowProductsModal
          item={previewItem}
          loading={loadingPreviewProducts}
          onClose={() => setPreviewItem(null)}
        />
        <DeleteQuickListModal
          showModalDeleteQuickList={showModalDeleteQuickList}
          setShowModalDeleteQuickList={setShowModalDeleteQuickList}
          handleConfirmDeleteQuickList={handleConfirmDeleteQuickList}
        />
      </div>
      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleClose}
      />
    </>
  )
}

export default SearchQuickList
